/**
 * Connection details, in one place, because they are needed in two.
 *
 * They started on their own page. But the thing people come here to do is copy
 * a base URL and a key, and putting that one click away from the model they are
 * looking at was one click too many — so the models list shows the same details
 * inline. Same component, so the two can never drift into disagreeing about
 * what to paste.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { fetchConnections, reissueKey, type Connection } from "./api";

export function Copy({ value, label = "copy" }: { value: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      className="cp"
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setDone(true);
          setTimeout(() => setDone(false), 1200);
        } catch {
          // Clipboard needs a secure context. Over plain HTTP on a LAN address
          // it throws, and silently doing nothing looks like a broken button.
          window.prompt("Copy this:", value);
        }
      }}
    >
      {done ? "copied" : label}
    </button>
  );
}

/**
 * Which environment variable carries the key.
 *
 * Matched by shape rather than by name. It used to be compared against the
 * literal `CUSTOMCLAW_API_KEY`, and when embedding connections started naming
 * `CUSTOMCLAW_EMBED_API_KEY` instead, the revealed key silently stopped being
 * substituted — so "copy all" handed over a block containing a masked key,
 * which fails at the far end for no visible reason.
 */
const isKeyVar = (name: string) => name.endsWith("API_KEY");

export function useKey(conn: Connection) {
  const queryClient = useQueryClient();
  const [revealed, setRevealed] = useState<string | null>(null);

  const reveal = useMutation({
    mutationFn: async () => {
      const all = await fetchConnections(true);
      // Matched on provider as well as name. Two cards can share a model name
      // — an Ollama model and a vLLM alias — and picking the first would show
      // one card the other's key, which works until someone pastes it.
      return (
        all.find((c) => c.model === conn.model && c.provider === conn.provider)
          ?.key ?? null
      );
    },
    onSuccess: setRevealed,
  });

  const rotate = useMutation({
    // The model goes along only for vLLM, where each model is its own server
    // with its own key. Ollama's one key covers every model, and naming one
    // there would be meaningless.
    mutationFn: () =>
      reissueKey(conn.provider, conn.provider === "vllm" ? conn.model : null),
    onSuccess: (data) => {
      setRevealed(data.key);
      void queryClient.invalidateQueries({ queryKey: ["connections"] });
    },
  });

  return { revealed, shown: revealed ?? conn.key_masked, reveal, rotate };
}

export function envText(conn: Connection, key: string): string {
  return conn.env
    .map((e) => `${e.name}=${isKeyVar(e.name) ? key : e.value}`)
    .join("\n");
}

export function ConnectionDetails({
  conn,
  keyState,
}: {
  conn: Connection;
  keyState: ReturnType<typeof useKey>;
}) {
  const { revealed, shown, reveal } = keyState;
  const block = envText(conn, shown);

  return (
    <>
      <dl className="kv">
        <dt>Base URL</dt>
        <dd>
          <span>{conn.base_url}</span>
          <Copy value={conn.base_url} />
        </dd>

        <dt>Model</dt>
        <dd>
          <span>{conn.model}</span>
          <Copy value={conn.model} />
        </dd>

        <dt>API key</dt>
        <dd>
          <span>{shown}</span>
          {revealed === null ? (
            <button className="cp" type="button" onClick={() => reveal.mutate()}>
              {reveal.isPending ? "…" : "reveal"}
            </button>
          ) : (
            <Copy value={revealed} />
          )}
        </dd>

        <dt>Format</dt>
        <dd>
          <span>{conn.api_format}</span>
        </dd>
      </dl>

      <div className="envblock">
        <p className="hint">Paste into the client's environment:</p>
        <pre>{block}</pre>
        <Copy label="copy all" value={block} />
      </div>

      {conn.note && <p className="conn-note">{conn.note}</p>}
    </>
  );
}
